import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import PhoneInput from 'react-phone-number-input';
import Swal from 'sweetalert2';
import { Eye, EyeSlash } from 'react-bootstrap-icons';

import Button from '../common/Button';
import { apiUrl } from '../../config/api';
import logo from '../../images/amazing gems and jewellery png with name.png';
import './LoginModal.scss';

const STEP = { DETAILS: 'details', OTP: 'otp', VERIFIED: 'verified' };

/**
 * Account sign-up: collect details → verify email by OTP → create account.
 *
 * The step flow used to be driven by `document.querySelector(...).style
 * .setProperty('display', ...)`, which React could overwrite on any
 * re-render and which left the form in whatever state it was last in.
 * It is plain state now.
 */
const SignupModal = ({ show, onClose }) => {
  const [step, setStep] = useState(STEP.DETAILS);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [contactNo, setContactNo] = useState('');
  const [otp, setOtp] = useState('');

  // Reset the whole flow on close so reopening always starts at step one.
  useEffect(() => {
    if (!show) {
      setStep(STEP.DETAILS);
      setPassword('');
      setConfirmPassword('');
      setOtp('');
      setFormError(null);
      setShowPassword(false);
    }
  }, [show]);

  const detailsComplete =
    name && email && contactNo && password && confirmPassword;

  const sendOtp = async () => {
    if (!detailsComplete) {
      setFormError('Please fill in every field before verifying your email.');
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }

    setLoading(true);
    setFormError(null);
    try {
      const response = await fetch(apiUrl('/api/send-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setFormError(data.message || 'We could not send the code. Try again.');
        return;
      }
      setStep(STEP.OTP);
      setOtp('');
    } catch {
      setFormError('We could not reach the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (event) => {
    event.preventDefault();
    if (!otp) {
      setFormError('Please enter the code we emailed you.');
      return;
    }

    setLoading(true);
    setFormError(null);
    try {
      const response = await fetch(apiUrl('/api/verify-otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setFormError(data.message || 'That code is not correct.');
        setOtp('');
        return;
      }
      setStep(STEP.VERIFIED);
    } catch {
      setFormError('We could not reach the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const createAccount = async (event) => {
    event.preventDefault();
    setLoading(true);
    setFormError(null);

    try {
      const response = await fetch(apiUrl('/api/user/signup'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          password,
          confirm_password: confirmPassword,
          contact_no: contactNo,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (!data.success || data.password === false) {
        setFormError(data.message || 'We could not create your account.');
        return;
      }

      onClose();
      Swal.fire({
        icon: 'success',
        confirmButtonColor: '#b63f23',
        title: 'Account created — please sign in to continue.',
      });
    } catch {
      setFormError('We could not reach the server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>Create an account</Modal.Header>
      <Modal.Body>
        <div className="ij-auth">
          <img className="ij-auth__logo" src={logo} alt="Indian Jewelryish" />

          <ol className="ij-auth__steps" aria-label="Sign-up progress">
            <li className={step === STEP.DETAILS ? 'is-active' : 'is-done'}>
              Your details
            </li>
            <li
              className={
                step === STEP.OTP
                  ? 'is-active'
                  : step === STEP.VERIFIED
                  ? 'is-done'
                  : ''
              }
            >
              Verify email
            </li>
            <li className={step === STEP.VERIFIED ? 'is-active' : ''}>Finish</li>
          </ol>

          <form className="ij-auth__form" onSubmit={createAccount} noValidate>
            <div className="ij-field">
              <label className="ij-label" htmlFor="signup-name">
                Full name
              </label>
              <input
                id="signup-name"
                type="text"
                className="ij-input"
                autoComplete="name"
                value={name}
                required
                disabled={loading || step !== STEP.DETAILS}
                onChange={(event) => setName(event.target.value)}
              />
            </div>

            <div className="ij-field">
              <label className="ij-label" htmlFor="signup-email">
                Email address
              </label>
              <input
                id="signup-email"
                type="email"
                className="ij-input"
                autoComplete="email"
                value={email}
                required
                disabled={loading || step !== STEP.DETAILS}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>

            <div className="ij-field">
              <label className="ij-label" htmlFor="signup-password">
                Password
              </label>
              <div className="ij-auth__password">
                <input
                  id="signup-password"
                  type={showPassword ? 'text' : 'password'}
                  className="ij-input"
                  autoComplete="new-password"
                  value={password}
                  required
                  disabled={loading || step !== STEP.DETAILS}
                  onChange={(event) => setPassword(event.target.value)}
                />
                <button
                  type="button"
                  className="ij-auth__password-toggle"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((visible) => !visible)}
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="ij-field">
              <label className="ij-label" htmlFor="signup-confirm">
                Confirm password
              </label>
              <input
                id="signup-confirm"
                type="password"
                className="ij-input"
                autoComplete="new-password"
                value={confirmPassword}
                required
                disabled={loading || step !== STEP.DETAILS}
                aria-invalid={
                  confirmPassword && confirmPassword !== password
                    ? 'true'
                    : undefined
                }
                onChange={(event) => setConfirmPassword(event.target.value)}
              />
              {confirmPassword && confirmPassword !== password && (
                <p className="ij-field__error">Passwords do not match.</p>
              )}
            </div>

            <div className="ij-field">
              <label className="ij-label" htmlFor="signup-phone">
                Phone number
              </label>
              <PhoneInput
                id="signup-phone"
                placeholder="Enter phone number"
                value={contactNo}
                onChange={setContactNo}
                disabled={loading || step !== STEP.DETAILS}
              />
            </div>

            {formError && (
              <p className="ij-alert ij-alert--error" role="alert">
                {formError}
              </p>
            )}

            {step === STEP.DETAILS && (
              <Button
                variant="primary"
                block
                loading={loading}
                loadingText="Sending code…"
                onClick={sendOtp}
              >
                Verify email
              </Button>
            )}

            {step === STEP.VERIFIED && (
              <>
                <p className="ij-alert ij-alert--success">
                  Email verified — you&apos;re all set.
                </p>
                <Button
                  type="submit"
                  variant="primary"
                  block
                  loading={loading}
                  loadingText="Creating account…"
                >
                  Create account
                </Button>
              </>
            )}
          </form>

          {step === STEP.OTP && (
            <form className="ij-auth__form" onSubmit={verifyOtp} noValidate>
              <div className="ij-field">
                <label className="ij-label" htmlFor="signup-otp">
                  Enter the code sent to {email}
                </label>
                <input
                  id="signup-otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className="ij-input"
                  value={otp}
                  required
                  disabled={loading}
                  onChange={(event) => setOtp(event.target.value)}
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                block
                loading={loading}
                loadingText="Verifying…"
              >
                Verify code
              </Button>
              <Button
                variant="ghost"
                block
                disabled={loading}
                onClick={sendOtp}
              >
                Resend code
              </Button>
            </form>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SignupModal;
